import { useEffect, useState } from "react";
import { PERMISSION_GROUPS, SUPER_ADMIN, type Permission } from "@zenforge/shared";
import { FullScreenLoader } from "../components.js";

interface Role {
  id: number;
  name: string;
  description: string | null;
  permissions: string[];
  isSystem: boolean;
}

/**
 * Roles as a list, permissions as a form.
 *
 * Every role's full grid used to render open at once — a page-length stack of
 * checkboxes even for the role you were not touching. Everywhere else in this
 * app (Team Members, Collections) is a list you scan, then a form you edit
 * one row in; this now matches that, and the previous behaviour becomes a
 * single "Edit permissions" modal per role.
 */
export function RolesEditor({ toast }: { toast: (message: string) => void }) {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");

  // The role whose permissions are being edited, plus the staged checkbox
  // state for it. Editing is a considered act — one role, saved once — not a
  // grid that writes on every tick and invalidates the server's role cache
  // mid-edit.
  const [editing, setEditing] = useState<Role | null>(null);
  const [draft, setDraft] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const res = await fetch("/api/v1/roles");
      if (!res.ok) throw new Error("Failed to load roles");
      const data: Role[] = await res.json();
      setRoles(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load roles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const openEdit = (role: Role) => {
    setEditing(role);
    setDraft([...role.permissions]);
  };

  const closeEdit = () => {
    setEditing(null);
    setDraft([]);
  };

  const isDirty = editing
    ? draft.length !== editing.permissions.length ||
      [...draft].sort().join() !== [...editing.permissions].sort().join()
    : false;

  const toggle = (permission: Permission) => {
    setDraft((prev) =>
      prev.includes(permission) ? prev.filter((p) => p !== permission) : [...prev, permission],
    );
  };

  const save = async () => {
    if (!editing) return;
    try {
      setSaving(true);
      const res = await fetch(`/api/v1/roles/${editing.id}/permissions`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ permissions: draft }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error ?? "Failed to save");
      toast(`Saved "${editing.name}" permissions`);
      closeEdit();
      await load();
    } catch (err) {
      toast(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const create = async () => {
    try {
      const res = await fetch("/api/v1/roles", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newName, description: newDescription, permissions: [] }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error ?? "Failed to create role");
      toast(`Created role "${newName}"`);
      setShowNew(false);
      setNewName("");
      setNewDescription("");
      await load();
    } catch (err) {
      toast(err instanceof Error ? err.message : "Failed to create role");
    }
  };

  const remove = async (role: Role) => {
    if (!confirm(`Delete the "${role.name}" role? This cannot be undone.`)) return;
    try {
      const res = await fetch(`/api/v1/roles/${role.id}`, { method: "DELETE" });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error ?? "Failed to delete role");
      toast(`Deleted role "${role.name}"`);
      await load();
    } catch (err) {
      toast(err instanceof Error ? err.message : "Failed to delete role");
    }
  };

  if (loading) return <FullScreenLoader label="Loading roles…" />;
  if (error) return <div className="notice error">{error}</div>;

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="row between">
        <p className="muted" style={{ margin: 0 }}>
          What each role may do. Changes take effect on the next request — nobody has to sign in
          again.
        </p>
        <button className="btn primary" onClick={() => setShowNew((v) => !v)}>
          {showNew ? "Cancel" : "+ New role"}
        </button>
      </div>

      {showNew && (
        <div className="card">
          <div className="row" style={{ gap: 12, alignItems: "flex-end" }}>
            <div className="field" style={{ flex: 1, margin: 0 }}>
              <label>Role name</label>
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Proofreader"
              />
            </div>
            <div className="field" style={{ flex: 2, margin: 0 }}>
              <label>Description</label>
              <input
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="What this role is for"
              />
            </div>
            <button className="btn primary" disabled={!newName.trim()} onClick={() => void create()}>
              Create
            </button>
          </div>
          <p className="muted mini" style={{ marginTop: 8, marginBottom: 0 }}>
            New roles start with no permissions. Edit them from the list below once created.
          </p>
        </div>
      )}

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
              <th>Permissions</th>
              <th>Type</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {roles.map((role) => {
              const locked = role.name === SUPER_ADMIN;
              return (
                <tr key={role.id}>
                  <td>
                    <strong>{role.name}</strong>
                  </td>
                  <td className="muted">{role.description || "—"}</td>
                  <td>
                    {locked ? (
                      <span className="badge approved mini" title="Always holds every permission">
                        All permissions
                      </span>
                    ) : (
                      <span className="muted mini">
                        {role.permissions.length} granted
                      </span>
                    )}
                  </td>
                  <td>{role.isSystem && <span className="badge pending mini">Built-in</span>}</td>
                  <td>
                    <div className="row" style={{ gap: 6 }}>
                      {locked ? (
                        <span
                          className="muted mini"
                          title={`${SUPER_ADMIN} cannot be edited — it is the way back in if a permission change locks everyone else out.`}
                        >
                          Cannot be edited
                        </span>
                      ) : (
                        <button
                          className="btn secondary"
                          style={{ padding: "2px 8px", fontSize: "0.8rem" }}
                          onClick={() => openEdit(role)}
                        >
                          Edit permissions
                        </button>
                      )}
                      {!role.isSystem && (
                        <button
                          className="btn danger"
                          style={{ padding: "2px 8px", fontSize: "0.8rem" }}
                          onClick={() => void remove(role)}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {roles.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center py-4">
                  No roles found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="modal-backdrop" onClick={closeEdit}>
          <div
            className="modal-card"
            style={{ maxWidth: 760, width: "95%", maxHeight: "88vh", overflowY: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="row between" style={{ alignItems: "flex-start", marginBottom: 4 }}>
              <div>
                <h3 style={{ margin: 0 }}>{editing.name}</h3>
                {editing.description && (
                  <div className="muted mini" style={{ marginTop: 2 }}>
                    {editing.description}
                  </div>
                )}
              </div>
              <button className="btn secondary" style={{ padding: "2px 8px", fontSize: "0.8rem" }} onClick={closeEdit}>
                ✕ Close
              </button>
            </div>

            <div className="perm-grid" style={{ marginTop: 16 }}>
              {PERMISSION_GROUPS.map((group) => (
                <div key={group.group}>
                  <div className="perm-group-label">{group.group}</div>
                  {group.permissions.map(({ permission, label, danger }) => (
                    <label key={permission} className="perm-item">
                      <input
                        type="checkbox"
                        checked={draft.includes(permission)}
                        onChange={() => toggle(permission)}
                      />
                      <span>
                        {label}
                        {danger && (
                          <span className="perm-danger" title="Irreversible or privileged">
                            !
                          </span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              ))}
            </div>

            <div className="row justify-end gap-2" style={{ marginTop: 20 }}>
              <button className="btn secondary" onClick={closeEdit}>
                Cancel
              </button>
              <button className="btn primary" disabled={!isDirty || saving} onClick={() => void save()}>
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
